import React from "react";
import { styled } from "@linaria/react";
import { color, fonts } from "@ui";
import replaceNewLines from "@ui/lib/replaceNewLines";
import type { Placement } from "@popperjs/core";
import { InformationLine } from "@ui/IconV2/react";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { LinkType } from "@ui/TooltipV2/Link";
import { getHelpCenterUrl } from "../../../lib/helpCenter";
import { translationLocale } from "../../../i18n/locale";

const StyledTooltip = styled(TooltipV2)`
  max-width: 320px;
`;

const ChildrenWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  text-align: justify;
`;

const OptionTitle = styled.div`
  font-weight: 600;
  margin-bottom: 5px;
`;

const OptionDescription = styled.p`
  font: ${fonts.body2};
`;

export interface Option {
  label: string;
  description: string;
  value: string;
}

interface P {
  title: string;
  description?: string;
  options?: Option[];
  link?: `/${string}`;
  children?: React.ReactElement;
  // arrowWidth?: number;
  placement?: Placement;
  openDelay?: number;
}

function InfoTooltip({
  children,
  title,
  description,
  options = [],
  link,
  // arrowWidth,
  placement,
  openDelay = 1200,
}: P) {
  return (
    <StyledTooltip
      openDelay={openDelay}
      placement={placement ?? "bottom"}
      overlay={
        <OverlayWrapper
          title={title}
          text={
            <Wrapper>
              {description ? replaceNewLines(description) : null}
              {options.map(option => (
                <div key={option.value}>
                  <OptionTitle>{option.label}</OptionTitle>
                  <OptionDescription>{replaceNewLines(option.description)}</OptionDescription>
                </div>
              ))}
            </Wrapper>
          }
          link={
            link
              ? {
                  value: getHelpCenterUrl(link, translationLocale),
                  type: LinkType.EXTERNAL_LINK,
                }
              : undefined
          }
        />
      }
    >
      <ChildrenWrapper>
        {children ?? <InformationLine color={color.carbon800} height={16} width={16} />}
      </ChildrenWrapper>
    </StyledTooltip>
  );
}

export default React.memo(InfoTooltip);
